// web/armory.ts — the COLONY EVOLUTION ARMORY: one screen that shows, per caste,
// the genes you've equipped, that caste's Power, and how many slots remain open —
// so a player can read their colony's progression at a glance.
//
// Pure + self-contained: it reconstructs "equipped genes" from the player's
// `applyGene` action log (the genome stacks applied genes permanently, so the log IS
// the equipped set) and reads per-caste Power from `session.powerSnapshot()`. It never
// touches the sim, the save, determinism, or any hash — a render-only read-model.
//
// SLOTS: the colony has no hard slot system yet, so the Armory frames each caste as
// six TYPE slots (one per gene type: Wing/Mandible/VenomGland/Carapace/Antenna/Limb).
// A slot is "filled" once a gene of that type has been applied; "open" otherwise.

import { GENE_TYPE_NAME, RARITY_NAME } from '../src/sim/geneData.ts';
import { TRAIT_NAME } from '../src/sim/personality.ts';
import { genePower } from '../src/sim/genePower.ts';
import { rarityPerk } from '../src/sim/rarityPerks.ts';
import { validCastesForType, casteCompatLabel } from '../src/sim/casteGenes.ts';
import { BOX_TIERS } from './evolutionBox.ts';
import type { PowerSnapshot } from './colonyPower.ts';

/** The number of gene TYPES = the number of slots per caste. */
export const SLOT_COUNT = GENE_TYPE_NAME.length;

/** One applied gene, as captured in the `applyGene` action log (by value). */
export interface AppliedGene {
  caste: number;
  type: number;       // gene type (the slot)
  rarity: number;
  traits: readonly number[]; // trait ids, repeated per stack
  name: string;
}

/** A single equipped gene, enriched for display. */
export interface ArmoryGene {
  name: string;
  type: number;
  rarity: number;
  power: number;       // value-based Power Score (rarity + mutations + synergy)
  traitTotal: number;
  sourceTier: number;  // inferred likely box tier (rarity → tier)
  description: string;  // human trait breakdown
  offCaste: boolean;    // applied to a caste this gene's type doesn't usefully suit
}

export interface ArmoryCaste {
  caste: number;
  power: number;
  genes: ArmoryGene[];
  slotsFilled: number;
  slotsOpen: number;
}

export interface ArmoryView {
  castes: ArmoryCaste[];   // Worker / Soldier / Flyer / Specialist (caste order 0..3)
  colonyPower: number;
  totalGenes: number;
}

function fmt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}
function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;'));
}

/** Value-based Power Score for an applied gene (expression isn't recoverable from the
 *  log, so it's omitted — the same rarity-dominant scale the auto-rules default uses). */
function geneValuePower(rarity: number, traits: readonly number[]): number {
  const distinct = new Set(traits).size;
  return genePower({
    rarity, mutationCount: traits.length, synergyCount: distinct,
    upgradeLevel: 0, evolutionTier: 0, exprPower: 0,
  });
}

/** "Venom ×2 · Carapace ×1" from the repeated-trait-id list. */
function describeTraits(traits: readonly number[]): string {
  if (traits.length === 0) return 'No mutations';
  const counts = new Map<number, number>();
  for (const t of traits) counts.set(t, (counts.get(t) ?? 0) + 1);
  const parts: string[] = [];
  for (const [t, n] of counts) parts.push(`${TRAIT_NAME[t] ?? `Trait ${t}`} ×${n}`);
  return parts.join(' · ');
}

/**
 * Build the Armory read-model from the equipped-gene log + the live power snapshot.
 * Pure: same inputs ⇒ same view.
 */
export function buildArmory(applied: readonly AppliedGene[], snapshot: PowerSnapshot): ArmoryView {
  const powerByCaste = [snapshot.worker, snapshot.soldier, snapshot.flyer, snapshot.specialist];
  const castes: ArmoryCaste[] = [];
  for (let c = 0; c < 4; c++) {
    const genes: ArmoryGene[] = [];
    const typesFilled = new Set<number>();
    for (const a of applied) {
      if (a.caste !== c) continue;
      typesFilled.add(a.type);
      genes.push({
        name: a.name,
        type: a.type,
        rarity: a.rarity,
        power: geneValuePower(a.rarity, a.traits),
        traitTotal: a.traits.length,
        sourceTier: a.rarity, // rarity → likely source tier (floors align 1:1)
        description: describeTraits(a.traits),
        // Off-caste: this body-part type doesn't suit caste c (e.g. a Wing on a Soldier) —
        // it still applies (a hint, not a restriction), but the Armory flags the mismatch.
        offCaste: !validCastesForType(a.type).includes(c),
      });
    }
    castes.push({
      caste: c,
      power: powerByCaste[c] ?? 0,
      genes,
      slotsFilled: typesFilled.size,
      slotsOpen: Math.max(0, SLOT_COUNT - typesFilled.size),
    });
  }
  return { castes, colonyPower: snapshot.colony, totalGenes: applied.filter((a) => a.caste >= 0 && a.caste < 4).length };
}

// --- view --------------------------------------------------------------------

/** Build the Armory overlay body from the view + the caste display names. */
export function armoryHTML(view: ArmoryView, casteNames: readonly string[]): string {
  const cards = view.castes.map((c) => casteCardHTML(c, casteNames[c.caste] ?? `Caste ${c.caste}`)).join('');
  const head =
    `<div class="armory-summary">` +
      `<div class="armory-total"><span class="mm-k">Total Colony Power</span><span class="mm-v mm-accent">⚡ ${fmt(view.colonyPower)}</span></div>` +
      `<span class="muted small">${view.totalGenes} gene${view.totalGenes === 1 ? '' : 's'} equipped across your castes — hover any gene for details.</span>` +
    `</div>`;
  return head + `<div class="armory-grid">${cards}</div>`;
}

function casteCardHTML(c: ArmoryCaste, name: string): string {
  let slots = '';
  for (let t = 0; t < SLOT_COUNT; t++) {
    slots += slotHTML(t, c.genes.filter((g) => g.type === t));
  }
  return (
    `<section class="armory-caste">` +
      `<header class="ac-head"><span class="ac-name">${esc(name)}s</span>` +
      `<span class="ac-power" title="Current caste Power Score">⚡ ${fmt(c.power)}</span></header>` +
      `<div class="ac-slots">${slots}</div>` +
      `<div class="ac-foot muted small">${c.slotsFilled}/${SLOT_COUNT} slots filled · ${c.slotsOpen} open · ${c.genes.length} gene${c.genes.length === 1 ? '' : 's'}</div>` +
    `</section>`
  );
}

/** Max gene chips shown per slot before collapsing the rest into a "+N more" pip — keeps a
 *  heavily-stacked slot (common late-game, when many small genes pile onto one type) legible. */
export const SLOT_CHIP_CAP = 4;

function slotHTML(type: number, genes: readonly ArmoryGene[]): string {
  const typeName = esc(GENE_TYPE_NAME[type] ?? `Type ${type}`);
  if (genes.length === 0) {
    return `<div class="ac-slot empty"><span class="slot-type">${typeName}</span><span class="slot-open">open</span></div>`;
  }
  // Show the STRONGEST few chips; collapse any overflow into a "+N more" pip so the slot
  // can't grow unbounded as a long colony stacks gene after gene onto the same type.
  const ranked = [...genes].sort((a, b) => b.power - a.power);
  const shown = ranked.slice(0, SLOT_CHIP_CAP);
  const extra = ranked.length - shown.length;
  const chips = shown.map(geneChipHTML).join('') +
    (extra > 0 ? `<span class="gene-chip more" title="${extra} more gene${extra === 1 ? '' : 's'} stacked on this slot">+${extra}</span>` : '');
  return `<div class="ac-slot filled"><span class="slot-type">${typeName}</span><div class="slot-genes">${chips}</div></div>`;
}

function geneChipHTML(g: ArmoryGene): string {
  const pk = rarityPerk(g.type, g.rarity);
  const perk = pk ? `<span class="tip-row tip-perk"><b>${esc(pk.name)}</b> — ${esc(pk.effect)}</span>` : '';
  // Off-caste mismatch: a small ⚠ marker on the chip + a tooltip note. The gene works (a
  // hint, not a restriction) but contributes off its intended caste's design.
  const offMark = g.offCaste ? '<span class="chip-offcaste" title="Off-caste — this gene fits a different caste">⚠</span>' : '';
  const offRow = g.offCaste
    ? `<span class="tip-row tip-offcaste">⚠ Off-caste — fits ${esc(casteCompatLabel(g.type))}</span>`
    : '';
  return (
    `<span class="gene-chip rarity-${g.rarity}${g.offCaste ? ' off-caste' : ''}" tabindex="0">⚡${g.power}${offMark}` +
      `<span class="gene-tip" role="tooltip">` +
        `<span class="tip-name">${esc(g.name)}</span>` +
        `<span class="tip-rarity rar-${g.rarity}">${esc(RARITY_NAME[g.rarity] ?? '?')}</span>` +
        perk +
        offRow +
        `<span class="tip-row">Power Score <b>${g.power}</b></span>` +
        `<span class="tip-row tip-desc">${esc(g.description)}</span>` +
        `<span class="tip-row muted">Likely source: ${esc(BOX_TIERS[g.sourceTier] ?? 'Evolution')} Box</span>` +
      `</span>` +
    `</span>`
  );
}
